/**
 * What the Inspector shows with nothing selected: an overview of the open
 * level, like an asset's details page. This is a portfolio before it's an
 * engine, so an empty selection still says what you're looking at: the
 * company or project, its years or tagline, where, and each position (zone)
 * held there.
 *
 * It reads the level as it is now (edits included), from the zones' own
 * content components, found by schema-independent keys (`Zone`, `Role`,
 * `Project`, `Degree`), so it is data about the level, not a component view
 * (rule 4).
 */
import type { LevelIndexEntry } from "../../assets/content";
import { enabledComponent } from "../../world/entity";
import type { EntityId, EntityRecord } from "../../world/entity";
import type { WorldSnapshot } from "../../world/World";
import { formatYearRange } from "../format/dates";

export interface LevelOverview {
  readonly kind: "Role" | "Project" | "Education";
  /** The company or project, as the Content Browser tile titles it. */
  readonly title: string;
  /** The level's virtual asset path, e.g. `/Roles/Riot_Games.level`. */
  readonly assetPath: string;
  /** A role level's years across its positions, or a project's tagline. */
  readonly summary: string;
  /** Where the positions were, each once, in the order met. */
  readonly locations: readonly string[];
  /** What the zones are: a role's Positions, a project's Showcase, a school's Degrees. */
  readonly zonesLabel: string;
  /** Each zone with content: a role level's newest first, a project's in level order. */
  readonly zones: readonly OverviewZone[];
}

export interface OverviewZone {
  readonly id: EntityId;
  /** The zone entity's name, as the Outliner shows it. */
  readonly name: string;
  /** The position's title, or the project's name. */
  readonly title: string;
  /** A position's or a degree's years; null for a project's showcase. */
  readonly detail: string | null;
}

interface RoleData {
  readonly title: string;
  readonly start: string;
  readonly end: string | null;
  readonly location: string;
}

interface ProjectData {
  readonly name: string;
  readonly tagline: string;
}

interface DegreeData {
  readonly degree: string;
  readonly years: string;
}

export function levelOverview(world: WorldSnapshot, level: LevelIndexEntry): LevelOverview {
  const zones = [...world.entities.values()].filter(
    (entity) => enabledComponent(entity, "Zone") !== undefined,
  );
  const header = { title: level.title, assetPath: level.assetPath };
  switch (level.kind) {
    case "role":
      return { kind: "Role", ...header, zonesLabel: "Positions", ...roleSummary(zones) };
    case "project":
      return { kind: "Project", ...header, zonesLabel: "Showcase", ...projectSummary(zones) };
    case "education":
      return {
        kind: "Education",
        ...header,
        zonesLabel: "Degrees",
        ...educationSummary(zones, level),
      };
  }
}

function roleSummary(zones: readonly EntityRecord[]) {
  const roles = zones.flatMap((zone) => {
    const role = enabledComponent(zone, "Role") as RoleData | undefined;
    return role ? [{ zone, role }] : [];
  });
  // Newest first, as the tiles list positions; "YYYY-MM" sorts as text.
  roles.sort((a, b) => b.role.start.localeCompare(a.role.start));

  return {
    summary: formatYearRange(roles.map(({ role }) => role)),
    locations: [...new Set(roles.map(({ role }) => role.location).filter(Boolean))],
    zones: roles.map(({ zone, role }) => ({
      id: zone.id,
      name: zone.name,
      title: role.title,
      detail: formatYearRange([role]),
    })),
  };
}

function projectSummary(zones: readonly EntityRecord[]) {
  const projects = zones.flatMap((zone) => {
    const project = enabledComponent(zone, "Project") as ProjectData | undefined;
    return project ? [{ zone, project }] : [];
  });

  return {
    summary: projects[0]?.project.tagline ?? "",
    locations: [],
    zones: projects.map(({ zone, project }) => ({
      id: zone.id,
      name: zone.name,
      title: project.name,
      detail: null,
    })),
  };
}

/** A school: its tagline (from the seed, as the tile shows it), and each degree with its years. */
function educationSummary(zones: readonly EntityRecord[], level: LevelIndexEntry) {
  const degrees = zones.flatMap((zone) => {
    const degree = enabledComponent(zone, "Degree") as DegreeData | undefined;
    return degree ? [{ zone, degree }] : [];
  });

  return {
    summary: level.subtitle,
    locations: [],
    zones: degrees.map(({ zone, degree }) => ({
      id: zone.id,
      name: zone.name,
      title: degree.degree,
      detail: degree.years,
    })),
  };
}

/** One of a project's links, for the overview: a label, where it goes, and what it shows. */
export interface OverviewLink {
  readonly label: string;
  readonly url: string;
  /** The address without its protocol, e.g. "seepluspl.us". */
  readonly text: string;
}

/**
 * A project level's links, from the seed (its `links`, e.g. `website`), in
 * the seed's order. The Project component doesn't carry them: they're about
 * the project, not something the level draws.
 */
export function projectLinks(
  seed: {
    readonly projects: readonly {
      readonly level: string;
      readonly links?: Readonly<Record<string, string>>;
    }[];
  },
  levelId: string,
): OverviewLink[] {
  const links = seed.projects.find((project) => project.level === levelId)?.links ?? {};
  return Object.entries(links).map(([key, url]) => ({
    label: key.charAt(0).toUpperCase() + key.slice(1),
    url,
    text: url.replace(/^https?:\/\//, "").replace(/\/$/, ""),
  }));
}
